import { Box, Card, IconButton, Paper, Typography } from '@mui/material'
import { Close } from '@mui/icons-material'
import { useMemo, useState } from 'react'
import ApiClient from '../../../backend/ApiClient'
import { IFormGroup } from '../../../definitions/FormGroup'
import { IFormField } from '../../../definitions/FormField'
import useQuery from '../../hooks/useQuery'
import EditorForm from '../form_builder/EditorForm'

interface P {
    schemaData?: IFormGroup[]
    onClose: () => void
}

const SchemaPreview = ({ schemaData, onClose }: P) => {
    const addEmptyListItems = (schema: IFormGroup[] | undefined, baseData: any): any => {
        if (!schema) return baseData
        const data = { ...baseData }

        const processField = (field: IFormField, parentData: any, parentKey: string = '') => {
            if (field.type === 'object' && field.objectFields) {
                const objectKey = parentKey ? field.key.replace(parentKey + '.', '') : field.key
                const keys = objectKey.split('.')
                let current = parentData

                for (let i = 0; i < keys.length - 1; i++) {
                    if (!current[keys[i]]) {
                        current[keys[i]] = {}
                    }
                    current = current[keys[i]]
                }

                if (!current[keys[keys.length - 1]]) {
                    current[keys[keys.length - 1]] = {}
                }

                field.objectFields.forEach(objectField => {
                    processField(objectField, parentData, '')
                })
            } else if (field.type === 'list') {
                const keys = field.key.split('.')
                let current = parentData

                for (let i = 0; i < keys.length - 1; i++) {
                    if (!current[keys[i]]) {
                        current[keys[i]] = {}
                    }
                    current = current[keys[i]]
                }

                const finalKey = keys[keys.length - 1]

                if (!current[finalKey] || current[finalKey].length === 0) {
                    const emptyItem = {}

                    if (field.childFields) {
                        field.childFields.forEach(childField => {
                            const relativeKey = childField.key.startsWith(field.key + '.')
                                ? childField.key.substring(field.key.length + 1)
                                : childField.key

                            if (childField.type === 'object' && childField.objectFields) {
                                const objKeys = relativeKey.split('.')
                                let objCurrent = emptyItem

                                for (let j = 0; j < objKeys.length - 1; j++) {
                                    if (!objCurrent[objKeys[j]]) {
                                        objCurrent[objKeys[j]] = {}
                                    }
                                    objCurrent = objCurrent[objKeys[j]]
                                }

                                objCurrent[objKeys[objKeys.length - 1]] = {}

                                childField.objectFields?.forEach(objField => {
                                    const objFieldRelativeKey = objField.key.startsWith(childField.key + '.')
                                        ? objField.key.substring(childField.key.length + 1)
                                        : objField.key

                                    if (objField.type === 'list') {
                                        const listKeys = objFieldRelativeKey.split('.')
                                        let listCurrent = objCurrent[objKeys[objKeys.length - 1]]

                                        for (let k = 0; k < listKeys.length - 1; k++) {
                                            if (!listCurrent[listKeys[k]]) {
                                                listCurrent[listKeys[k]] = {}
                                            }
                                            listCurrent = listCurrent[listKeys[k]]
                                        }

                                        listCurrent[listKeys[listKeys.length - 1]] = [{}]
                                    }
                                })
                            } else if (childField.type === 'list') {
                                const nestedKeys = relativeKey.split('.')
                                let nestedCurrent = emptyItem

                                for (let j = 0; j < nestedKeys.length - 1; j++) {
                                    if (!nestedCurrent[nestedKeys[j]]) {
                                        nestedCurrent[nestedKeys[j]] = {}
                                    }
                                    nestedCurrent = nestedCurrent[nestedKeys[j]]
                                }

                                nestedCurrent[nestedKeys[nestedKeys.length - 1]] = [{}]
                            }
                        })
                    }

                    current[finalKey] = [emptyItem]
                }
            }
        }

        schema.forEach(step => {
            step.fields.forEach(field => {
                processField(field, data)
            })
        })

        return data
    }
    // TODO: function for create fake data and move it in contract schema manager
    const initialData = useMemo(() => {
        const baseData = {
            qualityAdjustment: {
                qualityMeasures: [],
                adjustmentType: null
            },
            costBasis: [],
            endDate: null,
            signDate: null,
            status: {},
            name: 'Contract Preview',
            settlementConfigs: [],
            externalId: null,
            signatories: null,
            files: [],
            startDate: null,
            _signature: null,
            id: null,
            lineOfBusinesses: [],
            arrangementType: null,
            versioning: {
                isHead: false,
                version: 1,
                ancestorKey: 'contract-ancestor_6c2ac4e2-f3bf-4b0c-8810-1e15671ef3b6'
            },
            createdAt: null,
            wordsCount: null,
            lastUpdate: null,
            icd10Codes: [],
            number: null,
            schemaUUID: 'schema_48462508-731d-461f-b44f-6617cbdeff61',
            uuid: null,
            payer: null,
            businessSegments: [],
            counterparty: {
                name: null,
                description: null,
                id: null,
                type: null
            },
            legalEntity: null,
            type: null
        }

        return addEmptyListItems(schemaData, baseData)
    }, [schemaData])

    const [data, setData] = useState(initialData)
    const marginModalVertical = 50

    const { response: contractEditorOptions } = useQuery({
        queryKey: '',
        queryFn: async () => await ApiClient.getSystemConfigsCollection()
    })

    const handleSave = updatedData => {
        setData(updatedData)
        onClose()
    }

    const handleComplete = () => {
        console.log('Form completed')
    }

    return (
        <Paper
            sx={{
                margin: `${marginModalVertical}px auto ${marginModalVertical}px`,
                maxWidth: 780,
                height: '-webkit-fill-available',
                display: 'flex',
                flexDirection: 'column',
                padding: 2

            }}
            elevation={4}>

            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2
            }}>
                <Typography variant='h6' fontWeight='bold'>
                    Preview
                </Typography>

                <IconButton
                    onClick={onClose}
                    sx={{
                        'backgroundColor': 'action.hover',
                        '&:hover': {
                            backgroundColor: 'action.selected'
                        }
                    }}>
                    <Close fontSize='small' />
                </IconButton>
            </Box>

            <Card sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'auto',
                minHeight: 0
            }}>
                <Box sx={{
                    flex: 1,
                    overflow: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    mb: 4

                }}>
                    <EditorForm
                        data={data}
                        stringOptions={{
                            'settlementConfigs.exclusions.pharmacy': contractEditorOptions?.find(x => x.uuid === 'exclusions')?.values?.map(v => v.name) ?? [],
                            'settlementConfigs.exclusions.supplemental': contractEditorOptions?.find(x => x.uuid === 'exclusions')?.values?.map(v => v.name) ?? [],
                            'eligibilityRules.esrdExclusion': contractEditorOptions?.find(x => x.uuid === 'exclusions')?.values?.map(v => v.name) ?? [],
                            'eligibilityRules.transplantExclusion': contractEditorOptions?.find(x => x.uuid === 'exclusions')?.values?.map(v => v.name) ?? [],
                            'revenueClaimsCalc.includeSupplementalBenefits': contractEditorOptions?.find(x => x.uuid === 'exclusions')?.values?.map(v => v.name) ?? [],
                            'revenueClaimsCalc.includeReinsurance': contractEditorOptions?.find(x => x.uuid === 'exclusions')?.values?.map(v => v.name) ?? []
                        }}
                        scc={{
                            'type': contractEditorOptions?.find(x => x.uuid === 'contract-types').values ?? [],
                            'productType': contractEditorOptions?.find(x => x.uuid === 'product-types').values ?? [],
                            'qualityAdjustment.adjustmentType': contractEditorOptions?.find(x => x.uuid === 'adjustment-types').values ?? [],
                            'businessSegments': contractEditorOptions?.find(x => x.uuid === 'business-segments').values ?? [],
                            'settlementConfigs.type': contractEditorOptions?.find(x => x.uuid === 'settlement-types').values ?? []
                        }}
                        lightStepper={true}
                        schema={schemaData}
                        setData={setData}
                        onSave={handleSave}
                        onComplete={handleComplete}
                        isReadOnly
                    />
                </Box>
            </Card>
        </Paper>
    )
}

export default SchemaPreview
